<template>
  <div>
    <b-modal
      id="modal-role"
      ref="my-modal"
      cancel-variant="outline-secondary"
      :ok-title="btnText"
      :ok-only="finalStep"
      cancel-title="Close"
      :cancel-disabled="busy"
      :ok-disabled="busy"
      centered
      :title="$t('DEBTIA_ADD_LETTER_TEMPLATE')"
      size="lg"
      @ok="submitHandler"
    >
      <b-overlay
        :show="busy"
        no-wrap
      >
        <template #overlay>
          <div
            v-if="processing"
            class="text-center p-3 bg-primary text-light rounded"
          >
            <div class="mb-2">
              Please wait. Don't close the window.
            </div>
            <b-spinner
              type="grow"
              label="Loading..."
            />
          </div>
          <div
            v-else
            ref="dialog"
            tabindex="-1"
            role="dialog"
            aria-modal="false"
            aria-labelledby="form-confirm-label"
            class="text-center p-3"
          >
            <div v-if="spacedvariables">
              <b-card-text class="font-weight-bolder">
                {{ invalidDocMsg }}
              </b-card-text>
              <div class="spacedvariables">
                <b-list-group flush>
                  <b-list-group-item
                    v-for="(data, index) in spacedVariableList"
                    :key="index"
                  >
                    {{ data }}
                  </b-list-group-item>
                </b-list-group>
              </div>
            </div>
            <b-card-text
              v-else
              class="font-weight-bolder"
            >
              Please click OK if all the variables inside the document are correct and space free.
            </b-card-text>
            <div class="d-flex justify-content-end">
              <b-button
                v-ripple.400="'rgba(234, 84, 85, 0.15)'"
                variant="outline-danger"
                class="mr-3 cancel-btn"
                :class="spacedvariables ? 'cancel-btn-one' : ''"
                @click="onCancel"
              >
                {{ cancelBtn }}
              </b-button>
              <b-button
                v-if="!spacedvariables"
                v-ripple.400="'rgba(40, 199, 111, 0.15)'"
                variant="outline-success"
                @click="onOK"
              >
                OK
              </b-button>
            </div>
          </div>
        </template>
      </b-overlay>
      <b-form v-if="!showVariables && !finalStep">
        <validation-observer
          ref="lettertemplateform"
          tag="form"
        >
          <b-row>
            <b-alert
              v-if="showWarning"
              variant="warning"
              show
            >
              <div class="alert-body">
                <span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24px"
                    height="24px"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="feather feather-alert-circle"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                    />
                    <line
                      x1="12"
                      y1="8"
                      x2="12"
                      y2="12"
                    />
                    <line
                      x1="12"
                      y1="16"
                      x2="12.01"
                      y2="16"
                    />
                  </svg>
                  {{ warningMsg }} </span>
              </div>
            </b-alert>
            <b-col
              v-if="user_type === 'super_admin'"
              sm="12"
            >
              <b-form-group
                :label="$t('DEBTIA_COMPANY_NAME')"
                label-for="title"
                class="mb-2"
              >
                <b-form-input
                  id="title "
                  v-model="details.companyName"
                  type="text"
                  readonly
                />
              </b-form-group>
            </b-col>
            <b-col
              sm="12"
            >
              <validation-provider
                #default="{ errors }"
                name="Email Template"
                rules="required"
                vid="email_template_id"
              >
                <b-form-group
                  :label="$t('DEBTIA_EMAIL_TEMPLATE')"
                  label-for="email_template_id"
                  class="mb-2"
                  :state="errors.length > 0 ? false : null"
                >
                  <v-select
                    id="email_template_id"
                    v-model="details.email_template_id"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="templateOptions"
                    :reduce="val => val.value"
                    :clearable="false"
                    label="label"
                    :placeholder="$t('DEBTIA_EMAIL_TEMPLATE')"
                    input-id="email_template_id"
                    :class="{ 'is-invalid': errors.length > 0 }"
                  />
                  <b-form-invalid-feedback :state="errors.length > 0 ? false : null">
                    {{ errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              sm="12"
            >
              <validation-provider
                #default="{ errors }"
                name="Title"
                rules="required"
                vid="title"
              >
                <b-form-group
                  :label="$t('DEBTIA_TITLE')"
                  label-for="title"
                  class="mb-2"
                  :state="errors.length > 0 ? false : null"
                >
                  <b-form-input
                    id="title"
                    v-model="details.name"
                    type="text"
                    :placeholder="$t('DEBTIA_TITLE')"
                    :state="errors.length > 0 ? false : null"
                  />
                  <b-form-invalid-feedback :state="errors.length > 0 ? false : null">
                    {{ errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              sm="12"
            >
              <b-form-group
                :label="$t('DEBTIA_DOCUMENT_NAME')"
                label-for="document_name"
                class="mb-2"
              >
                <b-form-input
                  id="document_name"
                  v-model="details.document_name"
                  type="text"
                  :placeholder="$t('DEBTIA_DOCUMENT_NAME')"
                />
              </b-form-group>
            </b-col>
            <b-col
              sm="12"
            >
              <validation-provider
                #default="{ errors }"
                name="Description"
                rules="required"
                vid="description"
              >
                <b-form-group
                  :label="$t('DEBTIA_DESCRIPTION')"
                  label-for="description"
                  class="mb-2"
                  :state="errors.length > 0 ? false : null"
                >
                  <b-form-textarea
                    id="textarea-rows"
                    v-model="details.description"
                    :placeholder="$t('DEBTIA_DESCRIPTION')"
                    rows="8"
                    :state="errors.length > 0 ? false : null"
                  />
                  <b-form-invalid-feedback :state="errors.length > 0 ? false : null">
                    {{ errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              sm="12"
            >
              <b-form-group
                :label="$t('DEBTIA_FILE')"
                label-for="description"
                class="mb-2"
              >
                <b-form-file
                  id="file-default"
                  ref="letter-file"
                  accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </validation-observer>
      </b-form>
      <b-card
        v-else-if="showVariables && !finalStep"
        no-body
      >
        <!-- body -->
        <b-card-body class="letter-card">
          <b-card-title>Letter Details</b-card-title>
          <b-card-text>
            Below are the extracted variables:
          </b-card-text>
        </b-card-body>

        <!-- variableList -->
        <div class="variables">
          <b-list-group flush>
            <b-list-group-item
              v-for="(data, index) in variableList"
              :key="index"
              class="variableitem"
            >
              {{ data }}
            </b-list-group-item>
          </b-list-group>
        </div>
        <b-card-body>
          <b-card-text>
            Please click on confirm if all the variables are correct.
          </b-card-text>
        </b-card-body>
      </b-card>
      <div v-else>
        <b-card
          border-variant="success"
          bg-variant="transparent"
          class="shadow-none"
        >
          <b-card-text class="card-color">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24px"
              height="24px"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="feather feather-check-circle"
            >
              <path
                d="M22 11.08V12a10 10 0 1 1-5.93-9.14"
              />
              <polyline
                points="22 4 12 14.01 9 11.01"
              />
            </svg>
            Letter has been successfully added.
          </b-card-text>
        </b-card>
        <b-row>
          <b-col
            sm="6"
          >
            <h4>Sample Body Request </h4>
            <div>
              <pre>{{ apiBody }}</pre>
            </div>
          </b-col>
          <b-col
            sm="6"
          >
            <iframe
              :src="pdf"
              width="100%"
              height="545px"
            />
          </b-col>
        </b-row>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BForm,
  BOverlay,
  BFormTextarea,
  BFormFile,
  BAlert,
  BSpinner,
  BCardText,
  BButton,
  BCardTitle,
  BCardBody,
  BCard,
  BListGroup,
  BListGroupItem,
  BFormInvalidFeedback,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import vSelect from 'vue-select'
import axios from '@axios'
// import constants from '@/app-configs/constants'
import axiosemailv1 from '../../libs/axiosemailv1'
import axiosletter from '../../libs/axiosletter'

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BOverlay,
    BFormTextarea,
    BFormFile,
    BAlert,
    BSpinner,
    BCardText,
    BButton,
    BCardTitle,
    BCardBody,
    BCard,
    BListGroup,
    BListGroupItem,
    BFormInvalidFeedback,
    vSelect,
  },
  setup() {
    const templateOptions = []
    const requestArgs = [
      { request: '/email/get-templates', param: 'templateOptions' },
    ]
    return {
      templateOptions,
      requestArgs,
    }
  },
  directives: {
    Ripple,
  },
  props: {
    templateData: {
      type: Object,
      required: true,
    },
    reloadMethod: {
      type: Function,
      required: true,
    },
    modalLoads: {
      type: Boolean,
      required: true,
    },
    wordFile: {
      type: Boolean,
      required: true,
    },
    step: {
      type: Boolean,
      required: true,
    },
    variables: {
      type: Boolean,
      required: true,
    },
    clicked: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      finalStep: false,
      showVariables: false,
      busy: false,
      processing: false,
      counter: 1,
      interval: null,
      form: {
        name: '',
        subject: '',
        content: '',
        user_id: JSON.parse(localStorage.getItem('userData')).data.id,
        companyName: '',
      },
      templateDetails: {
        user_id: JSON.parse(localStorage.getItem('userData')).data.id,
      },
      snowOption: {
        theme: 'snow',
      },
      required,
      details: {
        id: 0,
        name: '',
        description: '',
        email_template_id: null,
      },
      user_type: JSON.parse(localStorage.getItem('userData')).data.user_type,
      showWarning: false,
      warningMsg: '',
      btnText: 'Save',
      letterfile: '',
      variableList: [],
      spacedVariableList: [],
      spacedvariables: false,
      cancelBtn: 'Cancel',
      filename: '',
      pdf: '',
      apiBody: '',
      invalidDocMsg: '',
      administratorIssue: false,
      communication_client_id: '',
      communication_secret: '',
      letter_client_id: '',
      letter_secret: '',
    }
  },
  watch: {
    templateData: {
      handler() {
        this.details = { ...this.templateData }
      },
    },
    wordFile: {
      handler() {
        this.showWarning = false
      },
    },
    step: {
      handler() {
        this.finalStep = false
      },
    },
    variables: {
      handler() {
        this.showVariables = false
      },
    },
    clicked: {
      handler() {
        this.details = {
          id: 0, name: '', description: '', email_template_id: null, document_name: '',
        }
      },
    },
  },
  beforeDestroy() {
    this.clearInterval()
  },
  created() {
    this.getCommunicationApiSettings()
    this.getLetterApiSettings()
  },
  methods: {
    async getLetterApiSettings() {
      await axios.get('/api-token/default?module=physical-letters').then(settings => {
        if (settings.status) {
          this.letter_client_id = settings.data.data.client_id
          this.letter_secret = settings.data.data.secret
        }
      })
    },
    async getCommunicationApiSettings() {
      await axios.get('/api-token/default?module=communication').then(settings => {
        if (settings.status) {
          this.communication_client_id = settings.data.data.client_id
          this.communication_secret = settings.data.data.secret
          this.getUserMultipleOptions()
        }
      })
    },
    clearInterval() {
      if (this.interval) {
        clearInterval(this.interval)
        this.interval = null
      }
    },
    submitHandler(evt) {
      evt.preventDefault()
      this.cancelBtn = 'Cancel'
      this.spacedvariables = false
      if (!this.showVariables) {
        this.$refs.lettertemplateform.validate().then(success => {
          if (!success) {
            return
          }
          const file = this.$refs['letter-file'].files[0]
          const filetype = file ? file.type : ''
          const filename = file ? file.name.toLowerCase() : ''
          if (!file
            || (filename && !filename.endsWith('.doc') && !filename.endsWith('.docx'))
            || (filetype && !filetype.includes('word'))) {
            this.warningMsg = 'Insert a Word document (.doc or .docx).'
            this.showWarning = true
            return
          }
          this.showWarning = false
          this.processing = false
          this.busy = true
        })
      } else if (!this.finalStep) {
        this.processing = true
        this.busy = true
        this.confirmation()
      } else {
        this.$nextTick(() => {
          this.$refs['my-modal'].toggle('#toggle-btn')
          this.details = {
            id: 0, name: '', description: '', document_name: '', email_template_id: null,
          }
        })
        this.reloadMethod()
      }
    },
    confirmation() {
      const headers = {
        clientId: this.letter_client_id,
        secret: this.letter_secret,
      }
      const url = '/letters-api/upload-letter'
      const form = new FormData()
      form.append('name', this.details.name)
      form.append('description', this.details.description)
      form.append('email_template_id', this.details.email_template_id)
      if (this.details.document_name) {
        form.append('document_name', this.details.document_name)
      }
      form.append('filename', this.filename)
      form.append('confirm', true)
      axiosletter.post(url, form, {
        headers,
      }).then(response => {
        if (response.status === 200) {
          setTimeout(() => {
            this.details = {
              id: 0, name: '', description: '', document_name: '', email_template_id: null,
            }
            this.busy = false
            this.processing = false
            this.variableList = []
            this.finalStep = true
            this.btnText = 'OK'
            this.pdf = response.data.data.pdfLink
            this.apiBody = response.data.data.apiBody
          }, 3000)
        }
      }).catch(
        error => {
          this.spacedVariableList = []
          setTimeout(() => {
            this.administratorIssue = true
            this.cancelBtn = 'OK'
            this.invalidDocMsg = error.response.data.message
            this.spacedvariables = true
            this.processing = false
            this.busy = true
          }, 3000)
        },
      )
    },
    onCancel() {
      this.busy = false
      if (this.administratorIssue) {
        this.$refs['my-modal'].hide()
        this.administratorIssue = false
      }
    },
    onOK() {
      this.counter = 1
      this.processing = true
      // Simulate an async request
      const headers = {
        'Content-Type': 'multipart/form-data',
        clientId: this.letter_client_id,
        secret: this.letter_secret,
      }
      const url = '/letters-api/upload-letter'
      const file = this.$refs['letter-file'].files[0]
      const form = new FormData()
      form.append('name', this.details.name)
      form.append('description', this.details.description)
      form.append('email_template_id', this.details.email_template_id)
      if (this.details.document_name) {
        form.append('document_name', this.details.document_name)
      }
      form.append('document', file)
      axiosletter.post(url, form, {
        headers,
      }).then(response => {
        if (response.status === 200) {
          setTimeout(() => {
            this.busy = false
            this.processing = false
            // this.showWarning = true
            // this.warningMsg = 'Variables are not correct.'
            if (!this.showVariables) {
              this.variableList = [...new Set(response.data.data.variables)]
              this.filename = response.data.data.filename
              this.showVariables = true
              this.btnText = 'Confirm'
            } else {
              this.variableList = []
              this.finalStep = true
              this.btnText = 'OK'
            }
          }, 3000)
          // this.reloadMethod()
        }
      }).catch(
        error => {
          this.$refs['letter-file'].reset()
          setTimeout(() => {
            this.spacedVariableList = error.response.data.data
            this.cancelBtn = 'OK'
            this.invalidDocMsg = error.response.data.message
            this.spacedvariables = true
            this.processing = false
            this.busy = true
          }, 3000)
        },
      )
    },
    async getUserMultipleOptions() {
      try {
        const headers = {
          clientId: this.communication_client_id,
          secret: this.communication_secret,
        }
        await Promise.all(
          this.requestArgs
            .map(REQUEST => axiosemailv1
              .get(REQUEST.request, {
                headers,
              })
              .then(res => {
                if (REQUEST.param === 'templateOptions') {
                  this.templateOptions = res.data.data.map(record => ({
                    value: record.id,
                    label: record.name,
                  }))
                }
              })),
        )
          .then(() => {
            this.loading = false
          })
      } catch (error) {
        console.log('options error', error)
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/quill.scss';
.ql-container.ql-snow{
  height: 150px !important;
}

.alert.alert-warning{
    width: 100%;
}

.cancel-btn, .variableitem {
  margin-left: 60px;
}

.cancel-btn {
  margin-right: 20px !important;
}

.cancel-btn-one {
  margin-top: 10px;
}

.letter-card {
  padding-bottom:5px;
}

.variables {
  overflow-y: scroll;
  max-height: 400px;
}

.spacedvariables {
  overflow-y: scroll;
  max-height: 300px;
}

.card-color {
  color : #1b922a;
}
</style>
